import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/tr';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { useSelector } from 'react-redux';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

moment.locale('tr');

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_MAP_TR = { "Monday": "P.tesi", "Tuesday": "Salı", "Wednesday": "Çarş.", "Thursday": "Perş.", "Friday": "Cuma", "Saturday": "C.tesi", "Sunday": "Pazar" };

export default function MallManagementPostBox() {
  const theme = useTheme();
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = "EPPK1";
  const token = parsedUser?.token;

  const [analyticsData, setAnalyticsData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);

  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: postBoxPermission, loading: permissionLoading } = useServicePermission(SERVICES.POST_BOX);

  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [heatmapDataType, setHeatmapDataType] = useState('insert');
  const [weeklyHeatmapDataType, setWeeklyHeatmapDataType] = useState('insert');
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);

  const [dailyUsageMetric, setDailyUsageMetric] = useState(0);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);
  const handleOpenImageModal = () => setImageModalOpen(true);
  const handleCloseImageModal = () => setImageModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'PostBox',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const fetchAnalyticsData = useCallback(async () => {
    if (permissionLoading) {
      return;
    }
    setLoadingAnalytics(true);
    try {
      if (!token || !mallID) { setLoadingAnalytics(false); return; }
      setHasPermission(postBoxPermission);

      if (postBoxPermission) { // <-- Koşul artık hook'tan gelen 'postBoxPermission'ı kontrol ediyor
        const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
        const firstDayOfMonth = moment().startOf('month').format('YYYY-MM-DD')
        const response = await Services.GetLiveAnalyticsPostBox(token, mallID, firstDayOfMonth, tomorrow);
        if (response.data.status && response.data.result) {
          setAnalyticsData(response.data.result);
          setTableData(response.data.result.postboxList ? Object.values(response.data.result.postboxList)[0] || [] : []);
        } else {
          toast.error(response.data.message || 'Veriler alınamadı.');
          setAnalyticsData({});
          setTableData([]);
        }
      } else {
        setAnalyticsData({});
        setTableData([]);
        setShowApplyBanner(true);
      }
    } catch (error) {
      toast.error('Analitik verileri alınırken bir hata oluştu.');
      setAnalyticsData({});
      setTableData([]);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [token, mallID, postBoxPermission, permissionLoading]);

  const fetchFilteredList = useCallback(async () => {
    if (!hasPermission) return;
    setLoadingTable(true);
    try {
      if (!token || !mallID) return;
      const response = await Services.GetLiveAnalyticsPostBoxList(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), mallID);
      if (response.data.status && response.data.result) {
        setTableData(response.data.result ? Object.values(response.data.result)[0] || [] : []);
      } else {
        toast.error(response.data.message || 'Liste verisi alınamadı.');
      }
    } catch (error) {
      toast.error('Liste verileri alınırken bir hata oluştu.');
    } finally {
      setLoadingTable(false);
    }
  }, [token, mallID, startDate, endDate, hasPermission]);

  useEffect(() => { fetchAnalyticsData(); }, [fetchAnalyticsData, postBoxPermission]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const { metrics, monthlyHeatmapChart, donutChart, weeklyStackChart, weeklyHeatmapChart } = useMemo(() => {
    const data = analyticsData || {};
    const { dailyUsageCount = 0, monthlyHourlyUsageTotalCount = {}, monthlyCargoFirmUsage = {}, weeklyDeliveryChoice = {}, weeklyHourlyUsageDensity = {}, postboxList } = data;

    const TOTAL_BOXES = 52;

    // İlk yüklenen listeyi al (filtrelemeden etkilenmeyen)
    const initialPostboxList = postboxList ? Object.values(postboxList)[0] || [] : [];
    const now = moment(); // Şu anki tarih ve saat

    const inUseCount = initialPostboxList.filter(item =>
      !item.endDate || moment(item.endDate).isAfter(now)
    ).length;

    const todayUsageCount = initialPostboxList.filter(item =>
      moment(item.startDate).isSame(now, 'day')
    ).length;

    const todayInUseCount = initialPostboxList.filter(item =>
      moment(item.startDate).isSame(now, 'day') && // Bugün başladı VE
      (!item.endDate || moment(item.endDate).isAfter(now)) // Hala kullanımda
    ).length;

    // Metrik 3: "Doluluk Oranı" için 'Boşta' sayısını hesapla
    const freeCount = TOTAL_BOXES - inUseCount;

    // --- 2. 'm' DİZİSİNİ BU HESAPLAMALARLA GÜNCELLİYORUZ ---

    const m = [
      {
        label: 'Göz Adedi',
        total: TOTAL_BOXES, // Statik 52
        used: inUseCount,   // Listeden hesaplanan "Kullanımda" sayısı
        icon: 'eva:layers-outline',
        gradient: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)'
      },
      {
        label: 'Günlük Kullanım Adedi (Bugün)',
        total: todayUsageCount, // Listeden hesaplanan "Bugünkü Kullanım" sayısı
        used: todayInUseCount,
        icon: 'eva:layers-outline',
        gradient: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)'
      },
      {
        label: 'Doluluk Oranı',
        total: TOTAL_BOXES, // Statik 52
        used: inUseCount,   // Listeden hesaplanan "Kullanımda" sayısı
        donutSeries: [freeCount, inUseCount], // Donut: [Boşta, Kullanımda]
        donutColors: ['#F7A937', '#3150A0']
      },
    ];

    const createHeatmap = (heatmapData, type) => {
      const dataForType = heatmapData[type] || {};
      const allTimeSlots = Array.from({ length: 15 }, (_, i) => { const h = 8 + i, n = h + 1; return `${h.toString().padStart(2, '0')}:00-${n.toString().padStart(2, '0')}:00`; });

      // Her saat için satır toplamlarını hesapla
      const hourlyTotals = allTimeSlots.map(timeSlot => {
        return DAY_ORDER.reduce((sum, dayKey) => {
          const apiDayData = Object.entries(dataForType).find(([key]) => key.toLowerCase() === dayKey.toLowerCase());
          const dayData = apiDayData ? apiDayData[1] : {};
          return sum + (dayData[timeSlot] || 0);
        }, 0);
      });

      // Her gün için sütun toplamlarını hesapla
      const dailyTotals = DAY_ORDER.map(dayKey => {
        const apiDayData = Object.entries(dataForType).find(([key]) => key.toLowerCase() === dayKey.toLowerCase());
        const dayData = apiDayData ? apiDayData[1] : {};
        return allTimeSlots.reduce((sum, timeSlot) => sum + (dayData[timeSlot] || 0), 0);
      });

      // Genel toplam
      const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

      // En alta günlük toplamlar satırı ekle
      const totalRow = {
        name: 'Toplam',
        data: [
          ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
          { x: 'Toplam', y: grandTotal }
        ]
      };

      // Series oluştur - her satıra "Toplam" sütunu ekle
      const series = [
        totalRow,
        ...allTimeSlots.map((timeSlot, index) => ({
          name: timeSlot.split('-')[0],
          data: [
            ...DAY_ORDER.map(dayKey => {
              const apiDayData = Object.entries(dataForType).find(([key]) => key.toLowerCase() === dayKey.toLowerCase());
              const dayData = apiDayData ? apiDayData[1] : {};
              return { x: DAY_MAP_TR[dayKey], y: dayData[timeSlot] || 0 };
            }),
            { x: 'Toplam', y: hourlyTotals[index] }
          ]
        })).reverse()
      ];

      const allCounts = Object.values(dataForType).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
      const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
      const insertColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
      const deliverColors = ['#D7D7D7', '#89D6CE', '#54C4B8', '#14B8A6', '#188B7E'];
      const activeColors = type === 'insert' ? insertColors : deliverColors;
      const options = {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 2,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: activeColors[0] },
                { from: 1, to: Math.floor(maxCount * 0.25), color: activeColors[2] },
                { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: activeColors[3] },
                { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: activeColors[4] }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#000'],
            fontSize: '12px',
            fontWeight: 500
          }
        },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet` } }
      };
      return { series, options, maxCount, colors: activeColors };
    };

    const monthlyHeat = createHeatmap(monthlyHourlyUsageTotalCount, heatmapDataType);
    const weeklyHeat = createHeatmap(weeklyHourlyUsageDensity, weeklyHeatmapDataType);

    const sortedFirms = Object.entries(monthlyCargoFirmUsage).sort(([, a], [, b]) => b - a);
    const topFirms = sortedFirms.slice(0, 5);
    const otherCount = sortedFirms.slice(5).reduce((sum, [, count]) => sum + count, 0);
    if (otherCount > 0) topFirms.push(['Diğer', otherCount]);
    const donut = {
      series: topFirms.map(([, count]) => count),
      options: { chart: { type: 'donut', toolbar: { show: false } }, labels: topFirms.map(([name]) => name), colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694', '#9061F9', '#9E9E9E'], plotOptions: { pie: { donut: { size: '65%' } } }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    };

    const processWeeklyChoice = (dataObject) => {
      const dailyCounts = Array(7).fill(0);
      if (!dataObject || typeof dataObject !== 'object') return dailyCounts;
      for (const date in dataObject) {
        const dayIndex = moment(date).day() === 0 ? 6 : moment(date).day() - 1;
        dailyCounts[dayIndex] = dataObject[date];
      }
      return dailyCounts;
    };
    const weeklyStack = {
      series: [{ name: 'Rezidans', data: processWeeklyChoice(weeklyDeliveryChoice.block) }, { name: 'Mağaza', data: processWeeklyChoice(weeklyDeliveryChoice.store) }],
      options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: { bar: { columnWidth: '30%' } }, colors: ['#1C64F2', '#FDBA8C'], dataLabels: { enabled: false }, xaxis: { categories: DAY_ORDER.map(d => DAY_MAP_TR[d]) }, legend: { position: 'bottom' } },
    };

    return { metrics: m, monthlyHeatmapChart: monthlyHeat, donutChart: donut, weeklyStackChart: weeklyStack, weeklyHeatmapChart: weeklyHeat };
  }, [analyticsData, heatmapDataType, weeklyHeatmapDataType]);

  const paginatedRows = useMemo(() => tableData.slice((page - 1) * rowsPerPage, page * rowsPerPage), [tableData, page, rowsPerPage]);
  const handleClearFilters = () => { if (!hasPermission) return; setStartDate(moment()); setEndDate(moment()); setTableData(analyticsData?.postboxList ? Object.values(analyticsData.postboxList)[0] || [] : []); };
  const maskName = (name) => { if (!name) return '*******'; const parts = name.split(' '); return parts.map(p => p.length > 1 ? p.charAt(0) + '*'.repeat(p.length - 1) : p).join(' '); };
  const maskPhone = (phone) => { if (!phone || phone.length < 10) return '**********'; return `${phone.slice(0, 3)}****${phone.slice(-3)}`; };
  const maskAddress = (address) => { if (!address) return '*******'; return address.split(' ').map(w => w.length <= 2 ? w : w.slice(0, 2) + '*'.repeat(w.length - 2)).join(' '); };

  if (loadingAnalytics || permissionLoading) { return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>; }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>PostBox</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Post Box</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="text" startIcon={<Iconify icon="eva:image-outline" />} sx={{ textTransform: 'none' }} onClick={handleOpenImageModal}>Tanıtım Görselleri</Button>
          <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {metrics.map((m, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ flex: 1 }}><Typography variant="subtitle2">{m.label}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{m.total}</strong></Typography><Typography variant="body2" color="text.secondary">Kullanımda: <strong>{m.used}</strong></Typography></Box>
              {m.donutSeries ? (<Box sx={{ width: 80, height: 80 }}><ReactApexChart options={{ chart: { sparkline: { enabled: true } }, plotOptions: { pie: { donut: { size: '65%' } } }, colors: m.donutColors, labels: ['Boşta', 'Kullanımda'], legend: { show: false } }} series={hasPermission ? m.donutSeries : zeroOutSeries(m.donutSeries)} type="donut" height={80} /></Box>) : (<Box sx={{ width: 40, height: 40, borderRadius: 1, background: m.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon={m.icon} width={20} height={20} sx={{ color: '#fff' }} /></Box>)}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}><Box><Typography variant="h6">Aylık Gün ve Saat Bazlı Kullanım Yoğunluğu</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Saat Aralığı</Typography></Box><FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Veri Tipi</InputLabel><Select value={heatmapDataType} label="Veri Tipi" onChange={(e) => setHeatmapDataType(e.target.value)}><MenuItem value="insert">Yükleme</MenuItem><MenuItem value="deliver">Teslim Alma</MenuItem></Select></FormControl></Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}><ReactApexChart options={monthlyHeatmapChart.options} series={hasPermission ? monthlyHeatmapChart.series : zeroOutSeries(monthlyHeatmapChart.series)} type="heatmap" height={280} /></Box>
              <HeatmapLegend maxCount={monthlyHeatmapChart.maxCount} colors={monthlyHeatmapChart.colors} label={heatmapDataType === "insert" ? "Yükleme" : 'Teslim'} />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Aylık PostBox Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Kargo Firması Dağılımı</Typography>
            <ReactApexChart options={donutChart.options} series={hasPermission ? donutChart.series : zeroOutSeries(donutChart.series)} type="donut" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Günlük Teslimat Tercihine Göre Kullanım</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Toplam Kullanım Adedi</Typography>
            <ReactApexChart options={weeklyStackChart.options} series={hasPermission ? weeklyStackChart.series : zeroOutSeries(weeklyStackChart.series)} type="bar" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}><Box><Typography variant="h6">Haftalık PostBox Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Saat Aralığı</Typography></Box><FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Veri Tipi</InputLabel><Select value={weeklyHeatmapDataType} label="Veri Tipi" onChange={(e) => setWeeklyHeatmapDataType(e.target.value)}><MenuItem value="insert">Yükleme</MenuItem><MenuItem value="deliver">Teslim Alma</MenuItem></Select></FormControl></Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}><ReactApexChart options={weeklyHeatmapChart.options} series={hasPermission ? weeklyHeatmapChart.series : zeroOutSeries(weeklyHeatmapChart.series)} type="heatmap" height={280} /></Box>
              <HeatmapLegend maxCount={weeklyHeatmapChart.maxCount} colors={weeklyHeatmapChart.colors} label={weeklyHeatmapDataType === "insert" ? "Yükleme" : 'Teslim'} />
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 4, p: 3, bgcolor: 'background.paper', position: 'relative' }} elevation={0}>
        {loadingTable && <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}><CircularProgress /></Box>}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2} marginLeft={'auto'}><Button fullWidth variant="outlined" size="medium" onClick={handleClearFilters} disabled={!hasPermission}>TEMİZLE</Button></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="contained" size="medium" onClick={fetchFilteredList} disabled={!hasPermission}>LİSTELE</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.paper' }}><TableRow>{['No', 'Lokasyon', 'Daire', 'Sahibi', 'Tel No', 'Başlangıç', 'Bitiş', 'Dolap No', 'Durum'].map((h) => (<TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>))}</TableRow></TableHead>
            <TableBody>
              {paginatedRows.map((r, index) => (<TableRow key={r.id || index} hover><TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell><TableCell>{r.locationCode}</TableCell><TableCell>{r.address}</TableCell><TableCell>{maskName(r.nameSurName)}</TableCell><TableCell>{maskPhone(r.gsmNumber)}</TableCell><TableCell>{moment(r.startDate).format('DD.MM.YY HH:mm')}</TableCell><TableCell>{r.endDate ? moment(r.endDate).format('DD.MM.YY HH:mm') : '-'}</TableCell><TableCell>{r.boxNumber}</TableCell><TableCell>{r.endDate ? 'Teslim Edildi' : 'Dolapta'}</TableCell></TableRow>))}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={9} sx={{ py: 2 }}><Box display="flex" alignItems="center" justifyContent="space-between"><Typography variant="caption">Toplam {tableData.length} kayıt</Typography><Pagination count={Math.ceil(tableData.length / rowsPerPage)} page={page} onChange={(e, newPage) => setPage(newPage)} shape="rounded" size="small" /></Box></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/posta-kutusu.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={isImageModalOpen} onClose={handleCloseImageModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Tanıtım Görselleri
          <IconButton aria-label="close" onClick={handleCloseImageModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="img" src="/assets/mallManagementImages/postbox.png" alt="PostBox Tanıtım Görseli" sx={{ width: '100%', height: 'auto', borderRadius: 1 }} />
        </DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}